import Link from 'next/link';
import type { PillarKey } from '@/lib/catalog/pillars';

/**
 * The short explainer that sits above the grid on a pillar-filtered listing.
 *
 * Why it lives here: the nav sends shoppers to /products?style=<pillar>, which
 * was a bare grid. The guidance that used to be on the landing pages was then
 * unreachable from the navigation. Rather than move the nav back, the buying
 * decision is explained where people actually land.
 *
 * The blackout tiers are the point. "Blackout" describes the FABRIC, not the
 * window: an inside-mount shade is cut narrower than the opening so it can move,
 * so a blackout fabric alone still leaves a line of light down each side. Only
 * side channels close that gap, which is what "100% blackout" actually means.
 * Every figure here is one the catalogue supports — side channels are a $100
 * option on 11 categories, capped at 110" of drop. No light-blocking percentage
 * is claimed for a fabric we have not measured.
 */

interface Tier {
  name: string;
  summary: string;
  detail: string;
}

const CONTENT: Partial<Record<PillarKey, { heading: string; intro: string; tiers: Tier[]; footnote?: string }>> = {
  blackout: {
    heading: 'Room darkening, blackout, or 100% blackout?',
    intro:
      'These are three different results, and the difference is not the fabric alone — it is what happens at the edges of the shade.',
    tiers: [
      {
        name: 'Room darkening',
        summary: 'Holds back most of the light.',
        detail:
          'Takes the glare and the brightness out of a room without making it dark. Good for a living room or a home office. You will still read by it at midday.',
      },
      {
        name: 'Blackout',
        summary: 'Nothing passes through the fabric.',
        detail:
          'A coated or foam-backed fabric that light cannot pass through. An inside-mount shade is cut narrower than the opening so it can travel, so a thin line of light still runs down each side and over the roll.',
      },
      {
        name: '100% blackout',
        summary: 'Blackout fabric plus sealed edges.',
        detail:
          'Add side channels and the fabric edge runs inside an aluminium track instead of beside it, so the side gap is covered rather than narrowed. This is what a nursery at nap time or a night-shift bedroom needs.',
      },
    ],
    footnote:
      'Side channels are a $100 option, available on 11 of our ranges and up to 110" of drop. They are specified when the shade is made, not retrofitted.',
  },
  'light-filtering': {
    heading: 'Sheer, light filtering, or room darkening?',
    intro: 'All three keep daylight in the room. They differ in how much of it, and how much privacy comes with it.',
    tiers: [
      {
        name: 'Sheer',
        summary: 'The most light, the most view.',
        detail:
          'An open weave that keeps the outlook and softens the sun. Daytime privacy only — after dark, a lit room is visible from outside.',
      },
      {
        name: 'Light filtering',
        summary: 'Diffuses the sun, keeps the glow.',
        detail:
          'Spreads daylight evenly across the room and takes the harshness out of it. Privacy through the day without the room feeling closed in.',
      },
      {
        name: 'Room darkening',
        summary: 'Most of the light, not all of it.',
        detail:
          'The dim end of this family, and the step before blackout. Useful where you want the room noticeably darker but not dark.',
      },
    ],
  },
};

export default function PillarExplainer({ pillar }: { pillar: PillarKey }) {
  const c = CONTENT[pillar];
  if (!c) return null;

  return (
    <section className="mb-8 rounded-2xl border border-sb-border bg-white p-6 md:p-7">
      <h2 className="text-xl font-bold tracking-tight text-heading-charcoal md:text-2xl">{c.heading}</h2>
      <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-gray-600">{c.intro}</p>

      <ol className="mt-5 grid gap-4 md:grid-cols-3">
        {c.tiers.map((t, i) => (
          <li key={t.name} className="rounded-xl border border-sb-border bg-sb-bg-alt p-5">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-bold tabular-nums text-primary-red">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="font-bold text-heading-charcoal">{t.name}</h3>
            </div>
            <p className="mt-1 text-sm font-medium text-gray-800">{t.summary}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{t.detail}</p>
          </li>
        ))}
      </ol>

      {c.footnote && (
        <p className="mt-4 border-l-2 border-primary-red/40 pl-3 text-xs leading-relaxed text-gray-500">
          {c.footnote}
        </p>
      )}

      {pillar === 'blackout' && (
        <p className="mt-4 text-sm text-gray-600">
          More detail in{' '}
          <Link href="/blackout-windows" className="font-medium text-primary-red underline underline-offset-2 hover:no-underline">
            how to make a window properly dark
          </Link>
          , or read the{' '}
          <Link href="/blackout-blinds" className="font-medium text-primary-red underline underline-offset-2 hover:no-underline">
            blackout buying guide
          </Link>
          .
        </p>
      )}
      {pillar === 'light-filtering' && (
        <p className="mt-4 text-sm text-gray-600">
          Not sure which way to go?{' '}
          <Link href="/light-filtering-vs-blackout" className="font-medium text-primary-red underline underline-offset-2 hover:no-underline">
            Light filtering compared with blackout
          </Link>
          .
        </p>
      )}
    </section>
  );
}
